// Доступ в «Документах» (023: contracts/api.md, «Схемы»; data-model.md; research, Р1–Р3): сводка
// доступа для строки списка, массовое изменение доступа к нескольким протоколам и доступ
// пользователя к протоколам в его карточке. Решает всегда сервер: схемы только описывают ответы
// и проверяют тело запроса.
import { z } from '../kernel/zod.ts';
import { IdSchema, PersonRefSchema, TimestampSchema } from '../kernel/common.ts';
import { EmailInputSchema, FullNameInputSchema } from '../access/user.ts';
import {
  ProtocolRoleSchema,
  SharingModeSchema,
  GrantZoneRecordRefSchema,
  ZoneRefListSchema,
  ZoneRecordSchema,
} from './access.ts';
import type { ProtocolRole } from './access.ts';

/** Человек с ролью в протоколе в сводке: без почты (data-model «Сводка»). */
export const AccessPersonSchema = z.object({
  userId: IdSchema,
  name: z.string(),
  role: ProtocolRoleSchema,
  /** Записи зоны с названиями — у редактора зоны, иначе пусто. */
  zone: z.array(ZoneRecordSchema),
  /** Почта не на корпоративном домене — метка «внешний» (022). */
  external: z.boolean(),
});
export type AccessPerson = z.infer<typeof AccessPersonSchema>;

/** Сводка доступа протокола для строки списка «Документов» (research, Р1). */
export const ProtocolAccessSummarySchema = z.object({
  documentId: IdSchema,
  sharing: SharingModeSchema,
  /** Все действующие роли, кроме строки владельца (как окно «Доступ» 020); по ФИО. */
  people: z.array(AccessPersonSchema),
  /** Пользователь сеанса управляет доступом к протоколу (minutes.access.manage). */
  canManage: z.boolean(),
  /** Роль пользователя сеанса в протоколе — «Открытые мне»; нет роли — null. */
  mine: z
    .object({
      role: ProtocolRoleSchema,
      grantedAt: TimestampSchema,
      grantedBy: PersonRefSchema,
    })
    .nullable(),
});
export type ProtocolAccessSummary = z.infer<typeof ProtocolAccessSummarySchema>;

export const AccessSummaryResponseSchema = z.object({
  items: z.array(ProtocolAccessSummarySchema),
});
export type AccessSummaryResponse = z.infer<typeof AccessSummaryResponseSchema>;

/** Протоколов за один массовый запрос (сценарий 3.4). */
export const BULK_ACCESS_MAX = 50;

/** Зона нужна редактору зоны и запрещена остальным ролям — как у GrantBody (020). */
function zoneMatchesRole(body: { role: ProtocolRole; zone?: readonly unknown[] | undefined }) {
  return body.role === 'zone_editor' ? (body.zone?.length ?? 0) > 0 : body.zone === undefined;
}
const zoneRoleMessage = {
  message: 'Зона задаётся только редактору зоны и обязательна для него',
  path: ['zone'],
};

/** Выдать или повысить роль пользователю — роль не понижается (research, Р2). */
const BulkUserSchema = z.strictObject({
  kind: z.literal('user'),
  userId: IdSchema,
  role: ProtocolRoleSchema,
  zone: ZoneRefListSchema.optional(),
  notify: z.boolean(),
});
/** Пригласить по почте: пользователь один, роль во всех протоколах, одно письмо-приглашение. */
const BulkInviteSchema = z.strictObject({
  kind: z.literal('invite'),
  email: EmailInputSchema,
  fullName: FullNameInputSchema.optional(),
  role: ProtocolRoleSchema,
  zone: ZoneRefListSchema.optional(),
});
/** Снять роль пользователя — архив строки доступа (принцип VII). */
const BulkRemoveSchema = z.strictObject({
  kind: z.literal('remove'),
  userId: IdSchema,
});

export const BulkAccessBodySchema = z
  .strictObject({
    documentIds: z
      .array(IdSchema)
      .min(1)
      .max(BULK_ACCESS_MAX)
      .refine((ids) => new Set(ids).size === ids.length, {
        message: 'Протокол указан дважды',
      }),
    person: z
      .discriminatedUnion('kind', [BulkUserSchema, BulkInviteSchema, BulkRemoveSchema])
      .optional(),
    /** Общий доступ; нет поля — «Не менять». */
    sharing: SharingModeSchema.optional(),
  })
  .refine((body) => body.person !== undefined || body.sharing !== undefined, {
    message: 'Выберите сотрудника или общий доступ',
  })
  .refine(
    (body) => body.person?.kind === 'remove' || !body.person || zoneMatchesRole(body.person),
    {
      ...zoneRoleMessage,
      path: ['person', 'zone'],
    },
  );
export type BulkAccessBody = z.infer<typeof BulkAccessBodySchema>;

/** Исход протокола (data-model «Исходы»). */
export const bulkOutcomes = ['changed', 'unchanged', 'skipped'] as const;
export type BulkOutcome = (typeof bulkOutcomes)[number];

/** Причина, по которой протокол не изменён. */
export const bulkReasons = [
  'already',
  'higher_role',
  'no_grant',
  'unavailable',
  'no_right',
  'trashed',
  'owner',
  'zone_full',
  'rate_limited',
  'failed',
] as const;
export type BulkReason = (typeof bulkReasons)[number];

export const BulkAccessItemSchema = z.object({
  documentId: IdSchema,
  outcome: z.enum(bulkOutcomes),
  reason: z.enum(bulkReasons).nullable(),
  /**
   * Состояние до изменения — у исхода changed, для «Отменить»: роль и зона цели (строки не было —
   * null) и общий доступ.
   */
  before: z
    .object({
      role: ProtocolRoleSchema.nullable(),
      // Зона выданной роли — организации и подразделения (030: человек в выдачу не идёт).
      zone: z.array(GrantZoneRecordRefSchema),
      sharing: SharingModeSchema,
    })
    .nullable(),
  /** Строка цели после изменения; роль снята или цели нет — null. */
  after: z.object({ grantId: IdSchema, version: z.number().int().min(1) }).nullable(),
});
export type BulkAccessItem = z.infer<typeof BulkAccessItemSchema>;

export const BulkAccessResultSchema = z.object({
  /** В порядке запроса; без номеров и названий протоколов — их знает экран. */
  items: z.array(BulkAccessItemSchema),
  changed: z.number().int().min(0),
  notChanged: z.number().int().min(0),
  /** Цель запроса: пользователь выдачи, снятия или приглашённый; только общий доступ — null. */
  userId: IdSchema.nullable(),
});
export type BulkAccessResult = z.infer<typeof BulkAccessResultSchema>;

/** Доступ пользователя к протоколам — раздел карточки в «Настройках → Пользователи». */
export const UserProtocolAccessSchema = z.object({
  items: z.array(
    z.object({
      documentId: IdSchema,
      number: z.number().int().min(1),
      title: z.string(),
      role: ProtocolRoleSchema,
      zone: z.array(ZoneRecordSchema),
      grantedAt: TimestampSchema,
      grantedBy: PersonRefSchema,
    }),
  ),
});
export type UserProtocolAccess = z.infer<typeof UserProtocolAccessSchema>;
